import React from 'react';
import { Download } from 'lucide-react';
import { downloadFile } from '../../../../utils/downloadFile';
import { detectTileType, getGenericLabel } from './detectTileType';
import FannedPages from './illustrations/FannedPages';
import WaveformBars from './illustrations/WaveformBars';
import PlayFrame from './illustrations/PlayFrame';
import StackedSlides from './illustrations/StackedSlides';
import CellGrid from './illustrations/CellGrid';
import MarkdownPage from './illustrations/MarkdownPage';
import GenericPage from './illustrations/GenericPage';
import AppWindow from './illustrations/AppWindow';
import './FileTile.css';

/**
 * FileTile — branded inline tile for non-image file attachments.
 *
 * Replaces the old ChatFileChiclet and the short-lived ChatPdfTile.
 * Used by:
 *   - ChatMessageContent.js  (finalized chat messages)     density="strip"
 *   - StreamingMessage.js    (streaming chat messages)     density="strip"
 *   - PostMediaCollage.js    (post feed — documents+audio) density="card" (default)
 *
 * Densities:
 *   "card"  — the original vertical tile (badge / illustration / footer).
 *             Default, so call sites that don't pass `density` are unchanged.
 *   "strip" — horizontal layout: illustration thumbnail on the left,
 *             filename + badge + size in the middle, CTA on the right.
 *             ~110px tall vs the card's ~200px. The strip self-hosts a
 *             container-query wrapper (.ft-strip-host) and re-densifies
 *             with the width it is GIVEN, not the viewport:
 *               >560px 112x88 thumb · 420-560px 96x76 · <420px 80x62 (CTA hidden)
 *
 * Click behavior (both densities):
 *   Default ("self" mode) dispatches a `mediaClick` window event, the
 *   same contract the old ChatFileChiclet used. FullPageMediaViewer and
 *   slideshow extraction pick it up unchanged.
 *   In PostMediaCollage, parent `.media-item` handles clicks — pass
 *   `clickMode="parent"` so the tile stays click-inert.
 *   Optional `onOpen` (Chat Files gallery, CEL-3473): called with the same
 *   `{ id, url }` INSTEAD of dispatching, for a host that opens its own list
 *   of files. Callers that don't pass it are unchanged.
 */

// Per-type config — badge label + color class + illustration component.
// Badge color classes are defined in FileTile.css as .ft-badge-{key}.
const TYPE_CONFIG = {
  pdf:      { label: 'PDF',   Illustration: FannedPages,   cta: 'Open' },
  audio:    { label: 'AUDIO', Illustration: WaveformBars,  cta: 'Play' },
  video:    { label: 'VIDEO', Illustration: PlayFrame,     cta: 'Play' },
  pptx:     { label: 'PPTX',  Illustration: StackedSlides, cta: 'Open' },
  xlsx:     { label: 'XLSX',  Illustration: CellGrid,      cta: 'Open' },
  markdown: { label: 'MD',    Illustration: MarkdownPage,  cta: 'Open' },
  app:      { label: 'APP',   Illustration: AppWindow,     cta: 'Open' },
  generic:  { label: null,    Illustration: GenericPage,   cta: 'Open' }, // label comes from file ext
};

/**
 * ILL_METRICS — browser-MEASURED painted-ink bounds of each illustration,
 * used only by density="strip" to scale + optically center the artwork
 * inside the thumbnail.
 *
 * Why measured and not the declared CSS box: several illustrations paint
 * OUTSIDE their container. FannedPages is a 78x88 box whose side pages are
 * translated ±6/22px and rotated 7deg (real paint 111.8x102.9 with shadow);
 * StackedSlides pins both slides to top:0 so its visible ink floats 6.66px
 * ABOVE the box center. Centering the container instead of the ink is what
 * made the PPTX strip look vertically off (Nitish, 2026-08-27).
 *
 *   iw/ih — painted width/height incl. box-shadow extents, +1px margin
 *   ox/oy — offset of the ink center from the declared box center
 *           (positive = ink sits right/below; CSS translates by -ox/-oy)
 *
 * Re-measure when editing any illustration: scripts/filetile_ink_qc/
 * renders each one and asserts painted bounds match this table.
 */
const ILL_METRICS = {
  pdf:      { iw: 113, ih: 104, ox: 0,     oy: 0 },
  audio:    { iw: 141, ih: 89,  ox: 0,     oy: 0 },
  video:    { iw: 145, ih: 99,  ox: 0,     oy: 3 },
  pptx:     { iw: 123, ih: 81,  ox: -2.21, oy: -6.66 },
  xlsx:     { iw: 123, ih: 95,  ox: 0,     oy: 2 },
  markdown: { iw: 109, ih: 93,  ox: 0,     oy: 2 },
  generic:  { iw: 91,  ih: 99,  ox: 0,     oy: 2 },
  app:      { iw: 97,  ih: 99,  ox: 0,     oy: 2 },
};

export function formatFileSize(bytes) {
  if (!bytes || bytes <= 0) return '';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  if (bytes < 1024 * 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  return (bytes / (1024 * 1024 * 1024)).toFixed(1) + ' GB';
}

const FileTile = ({
  fileInfo,
  mediaId,
  messageIndex,
  mediaIndex,
  clickMode = 'self',
  // Optional explicit type override (e.g. 'app') for tiles that aren't files with a mime/ext.
  // When omitted, the type is detected from mimeType/filename as before.
  type: typeOverride = null,
  // For clickMode 'external': the URL to open in a new tab (e.g. a CellCog app link).
  href = null,
  // "card" (default, original tile) | "strip" (compact horizontal preview strip)
  density = 'card',
  // Optional: called with { id, url } instead of dispatching mediaClick (see the header).
  onOpen = null,
  // Optional (Chat Files gallery, card density): a node on its own line under the badge row (e.g. the "Uploaded"
  // chip). Callers that don't pass it render exactly as before.
  belowTop = null,
  // Optional (Chat Files gallery, card density, Nitish Oct 16 2026): no "Open" / "Play" label in the footer, only the
  // Download button (the whole card already opens). Callers that don't pass it render exactly as before.
  hideCta = false,
}) => {
  const { url, filename, mimeType, fileSize } = fileInfo || {};
  const type = typeOverride || detectTileType(mimeType, filename);
  const config = TYPE_CONFIG[type] || TYPE_CONFIG.generic;

  const sizeLabel = formatFileSize(fileSize);
  const tileMediaId = mediaId || `msg-${messageIndex}-media-${mediaIndex}`;
  const isParentClick = clickMode === 'parent';
  const isExternalClick = clickMode === 'external';
  const isStrip = density === 'strip';

  // Generic tile shows the file extension in place of a type label.
  const isGeneric = type === 'generic';
  const badgeContent = isGeneric
    ? getGenericLabel(filename)
    : config.label;

  const handleClick = () => {
    if (isParentClick) return;
    if (isExternalClick) {
      // App / external link → open in a new tab (mirrors the old "View App" button).
      if (href) window.open(href, '_blank', 'noopener,noreferrer');
      return;
    }
    if (onOpen) {
      onOpen({ id: tileMediaId, url });
      return;
    }
    window.dispatchEvent(new CustomEvent('mediaClick', {
      detail: { id: tileMediaId, url },
    }));
  };

  // Download button (Nitish, Sep 19 2026): every SHOW_FILE render saves with one click; a right-click "Save video as"
  // is not obvious to non-developers. Stops the tile's own click (which opens the viewer). Hidden in parent-click mode
  // (post collages), where the tile is inert by design.
  const showDownload = !isParentClick && !isExternalClick && !!url;
  const handleDownload = (e) => {
    e.stopPropagation();
    e.preventDefault();
    downloadFile(url, filename);
  };
  const downloadButton = showDownload ? (
    <button
      type="button"
      className="ft-download"
      title="Download"
      aria-label={`Download ${filename || 'file'}`}
      onClick={handleDownload}
      onKeyDown={(e) => e.stopPropagation()}
    >
      <Download size={14} strokeWidth={2.25} />
    </button>
  ) : null;

  const handleKeyDown = (e) => {
    if (isParentClick) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick();
    }
  };

  // Class string — base + type modifier + mode modifier.
  const className = [
    isStrip ? 'ft-strip' : 'file-tile',
    `ft-${type}`,
    isParentClick ? 'ft-parent-click' : 'ft-self-click',
  ].join(' ');

  // Root element differs by click mode:
  //   self-click / external-click: interactive div with button semantics
  //   parent-click: plain div, pointer events disabled via CSS
  const rootProps = isParentClick
    ? { className, 'aria-hidden': true }
    : {
        className,
        onClick: handleClick,
        onKeyDown: handleKeyDown,
        role: 'button',
        tabIndex: 0,
        'aria-label': isExternalClick
          ? `Open app: ${filename}`
          : `Open ${config.label || 'file'}: ${filename}`,
      };

  if (isStrip) {
    const m = ILL_METRICS[type] || ILL_METRICS.generic;
    // Measured ink bounds + optical offset as custom properties; FileTile.css
    // computes the scale from these and the (container-query-set) thumb size.
    const illVars = { '--iw': m.iw, '--ih': m.ih, '--ox': m.ox, '--oy': m.oy };
    return (
      <div className="ft-strip-host">
        <div {...rootProps} style={illVars}>
          <span className="st-thumb">
            <span className="st-ill">
              <config.Illustration />
            </span>
          </span>
          <span className="st-body">
            <span className="st-name" title={filename}>{filename}</span>
            <span className="st-meta">
              <span className={`ft-badge ft-badge-${type}`}>
                {!isGeneric && <span className="ft-badge-dot" />}
                {badgeContent}
              </span>
              {sizeLabel && <span className="ft-size">{sizeLabel}</span>}
            </span>
          </span>
          {downloadButton && <span className="st-dl">{downloadButton}</span>}
          <span className="st-cta" aria-hidden="true">
            <span className="ft-cta">{config.cta}</span>
          </span>
        </div>
      </div>
    );
  }

  return (
    <div {...rootProps}>
      <div className="ft-top">
        <span className={`ft-badge ft-badge-${type}`}>
          {!isGeneric && <span className="ft-badge-dot" />}
          {badgeContent}
        </span>
        {sizeLabel && <span className="ft-size">{sizeLabel}</span>}
      </div>
      {belowTop}

      <div className="ft-illustration">
        <config.Illustration />
      </div>

      <div className="ft-footer">
        <span className="ft-filename" title={filename}>{filename}</span>
        {downloadButton}
        {!hideCta && <span className="ft-cta" aria-hidden="true">{config.cta}</span>}
      </div>
    </div>
  );
};

export default FileTile;
